import React, { useState, useEffect } from 'react';
import { historyApi } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import ActivityLogPanel from '../components/ActivityLogPanel';
import { useAuth } from '../context/AuthContext';
import { 
  History as HistoryIcon, 
  Calendar,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, getWeekRange } from '../lib/utils';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';

export default function History() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await historyApi.getWeeks();
      setHistory(res.data);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  // Calculate trends
  const chartData = [...history].reverse().map(w => ({
    week: w.week_start,
    gross: w.total_gross,
    net: w.total_net,
  }));

  // Calculate overall stats
  const totalGross = history.reduce((sum, w) => sum + w.total_gross, 0);
  const totalNet = history.reduce((sum, w) => sum + w.total_net, 0);
  const totalLoads = history.reduce((sum, w) => sum + w.load_count, 0);
  const avgWeeklyNet = history.length > 0 ? totalNet / history.length : 0;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="history-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="history-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-manrope font-bold text-foreground">History</h1>
        <p className="text-muted-foreground mt-1">View past weeks and track performance over time</p>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Weeks Tracked</p>
                <p className="text-xl font-bold">{history.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Gross</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(totalGross)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <TrendingDown className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Net</p>
                <p className="text-xl font-bold font-mono">{formatCurrency(totalNet)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Package className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Loads</p>
                <p className="text-xl font-bold">{totalLoads}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-manrope">Earnings Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="grossGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="netGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="week" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => {
                    const date = new Date(val);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(val) => getWeekRange(val)}
                />
                <Area 
                  type="monotone" 
                  dataKey="gross" 
                  stroke="#2563eb" 
                  fill="url(#grossGradient)"
                  strokeWidth={2}
                  name="Gross"
                />
                <Area 
                  type="monotone" 
                  dataKey="net" 
                  stroke="#10b981" 
                  fill="url(#netGradient)"
                  strokeWidth={2}
                  name="Net"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Weekly History List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-manrope">Weekly Records</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <HistoryIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No history yet. Complete your first week!</p>
            </div>
          ) : (
            <div className="divide-y">
              {history.map((week, index) => (
                <div 
                  key={week.week_start}
                  className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/summary?week=${week.week_start}`)}
                  data-testid={`history-week-${week.week_start}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Calendar className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium">{getWeekRange(week.week_start)}</p>
                      <p className="text-sm text-muted-foreground">{week.load_count} loads</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Gross</p>
                      <p className="font-mono font-medium">{formatCurrency(week.total_gross)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Net</p>
                      <p className="font-mono font-medium text-emerald-600">{formatCurrency(week.total_net)}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin-only Activity History (compact panel).  Non-admins get a
          friendly forbidden card from the backend gate. */}
      {isAdmin && (
        <div data-testid="admin-activity-section">
          <ActivityLogPanel variant="full" source="all" />
        </div>
      )}

      {/* Average Stats */}
      {history.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Average Weekly Net Payout</p>
                <p className="text-3xl font-bold font-mono text-blue-800">
                  {formatCurrency(avgWeeklyNet)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-700">Projected Monthly</p>
                <p className="text-2xl font-bold font-mono text-blue-800">
                  {formatCurrency(avgWeeklyNet * 4.33)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
